// src/Builder.ts
import { assertEx } from "@xylabs/assert";
import { exists } from "@xylabs/exists";
import {
  isJsonObject,
  omitByPrefix,
  pickByPrefix,
  toJson
} from "@xylabs/object";
import { ObjectHasher, removeEmptyFields } from "@xyo-network/hash";
import {
  SequenceComparer,
  SequenceParser
} from "@xyo-network/payload-model";
var omitSchema = (payload) => {
  const result = structuredClone(payload);
  delete result.schema;
  return result;
};
var PayloadBuilder = class _PayloadBuilder {
  constructor(options) {
    this.options = options;
    const { schema } = options;
    this._schema = schema;
  }
  _fields;
  _meta;
  _schema;
  static async addStorageMeta(payloads, index = 0) {
    return Array.isArray(payloads) ? await (async () => {
      const timestamp = Date.now();
      return (await Promise.all(payloads.map(async (payload, i) => await this.addSequencedStorageMeta(
        payload,
        timestamp,
        i
      )))).sort(this.compareStorageMeta);
    })() : this.addSequencedStorageMeta(
      payloads,
      index
    );
  }
  static compareStorageMeta(a, b, comparer = SequenceComparer.local) {
    return comparer(a._sequence, b._sequence);
  }
  static async dataHash(payload) {
    return await ObjectHasher.hash(this.omitMeta(payload));
  }
  static async dataHashPairs(payloads) {
    return await Promise.all(
      payloads.map(async (payload) => {
        const dataHash = await this.dataHash(payload);
        return [payload, dataHash];
      })
    );
  }
  static dataHashableFields(schema, payload) {
    const cleanFields = removeEmptyFields({ ...payload, schema });
    assertEx(
      cleanFields == void 0 || isJsonObject(cleanFields),
      () => `Fields must be JsonObject: ${JSON.stringify(toJson(cleanFields), null, 2)}`
    );
    return this.omitMeta(cleanFields);
  }
  static async dataHashes(payloads) {
    return payloads ? await Promise.all(
      payloads.map(async (payload) => {
        return await this.dataHash(payload);
      })
    ) : void 0;
  }
  static async filterExclude(payloads = [], hash) {
    return await ObjectHasher.filterExcludeByHash(await this.filterExcludeByDataHash(payloads, hash), hash);
  }
  static async filterExcludeByDataHash(payloads = [], hash) {
    const hashes = Array.isArray(hash) ? hash : [hash];
    return (await this.dataHashPairs(payloads)).filter(([_, objHash]) => !hashes.includes(objHash))?.map((pair) => pair[0]);
  }
  static async filterIncludeByDataHash(payloads = [], hash) {
    const hashes = Array.isArray(hash) ? hash : [hash];
    return (await this.dataHashPairs(payloads)).filter(([_, objHash]) => hashes.includes(objHash))?.map((pair) => pair[0]);
  }
  static async filterIncludeByEitherHash(payloads = [], hash) {
    const hashes = Array.isArray(hash) ? hash : [hash];
    const map = await _PayloadBuilder.toAllHashMap(payloads);
    return hashes.map((hash2) => map[hash2]).filter(exists);
  }
  static async findByDataHash(payloads = [], hash) {
    return (await this.dataHashPairs(payloads)).find(([_, objHash]) => objHash === hash)?.[0];
  }
  static async hash(payload) {
    return await ObjectHasher.hash(this.omitStorageMeta(payload));
  }
  /**
   * Creates an array of payload/hash tuples based on the payloads passed in
   * @param objs Any array of payloads
   * @returns An array of payload/hash tuples
   */
  static async hashPairs(payloads) {
    return await Promise.all(
      payloads.map(async (payload) => {
        return [payload, await this.hash(payload)];
      })
    );
  }
  static hashableFields(payload) {
    return this.omitStorageMeta(payload);
  }
  static async hashes(payloads) {
    return await ObjectHasher.hashes(payloads);
  }
  static omitClientMeta(payloads, maxDepth = 1) {
    return Array.isArray(payloads) ? payloads.map((payload) => this.omitClientMeta(payload, maxDepth)) : omitByPrefix(payloads, "$", maxDepth);
  }
  static omitMeta(payloads, maxDepth = 1) {
    return Array.isArray(payloads) ? payloads.map((payload) => this.omitMeta(payload, maxDepth)) : this.omitStorageMeta(this.omitClientMeta(payloads, maxDepth), maxDepth);
  }
  static omitPrivateStorageMeta(payloads, maxDepth = 1) {
    return Array.isArray(payloads) ? payloads.map((payload) => this.omitPrivateStorageMeta(payload, maxDepth)) : omitByPrefix(payloads, "__", maxDepth);
  }
  static omitStorageMeta(payloads, maxDepth = 1) {
    return Array.isArray(payloads) ? payloads.map((payload) => this.omitStorageMeta(payload, maxDepth)) : omitByPrefix(payloads, "_", maxDepth);
  }
  static pickClientMeta(payloads, maxDepth = 1) {
    return Array.isArray(payloads) ? payloads.map((payload) => this.pickClientMeta(payload, maxDepth)) : pickByPrefix(payloads, "$", maxDepth);
  }
  static sortByStorageMeta(payloads, direction = 1, comparer = SequenceComparer.local) {
    return payloads.sort((a, b) => direction * comparer(a._sequence, b._sequence));
  }
  static async toAllHashMap(payloads) {
    const result = {};
    for (const pair of await this.hashPairs(payloads)) {
      const dataHash = await this.dataHash(pair[0]);
      result[pair[1]] = pair[0];
      result[dataHash] = pair[0];
    }
    return result;
  }
  static async toDataHashMap(objs) {
    const result = {};
    for (const pair of await this.dataHashPairs(objs)) {
      result[pair[1]] = pair[0];
    }
    return result;
  }
  /**
   * Creates an object map of payload hashes to payloads based on the payloads passed in
   * @param objs Any array of payloads
   * @returns A map of hashes to payloads
   */
  static async toHashMap(objs) {
    const result = {};
    for (const pair of await this.hashPairs(objs)) {
      result[pair[1]] = pair[0];
    }
    return result;
  }
  static async addHashMeta(payloads) {
    if (Array.isArray(payloads)) {
      return await Promise.all(
        payloads.map(async (payload) => {
          return await this.addHashMeta(payload);
        })
      );
    } else {
      const _hash = await this.hash(payloads);
      const _dataHash = await this.dataHash(payloads);
      return {
        ...payloads,
        _dataHash,
        _hash
      };
    }
  }
  static async addSequencedStorageMeta(payload, timestamp, index = 0) {
    const withHashMeta = await this.addHashMeta(payload);
    const _sequence = SequenceParser.from(timestamp, withHashMeta._hash, index).localSequence;
    return {
      ...withHashMeta,
      _sequence
    };
  }
  build() {
    return {
      schema: this._schema,
      ...this._fields,
      ...this._meta
    };
  }
  async dataHashableFields() {
    return await _PayloadBuilder.dataHashableFields(
      assertEx(this._schema, () => "Payload: Missing Schema"),
      // TODO: Add verification that required fields are present
      this._fields
    );
  }
  fields(fields) {
    this._fields = _PayloadBuilder.omitClientMeta(
      _PayloadBuilder.omitStorageMeta(
        omitSchema(
          removeEmptyFields(structuredClone(fields))
        )
      )
    );
    return this;
  }
  meta(meta) {
    this._meta = pickByPrefix(meta, "$");
    return this;
  }
  schema(value) {
    this._schema = value;
  }
};
export {
  PayloadBuilder,
  omitSchema
};
//# sourceMappingURL=index.mjs.map